import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  IconAlertTriangle,
  IconClipboardCheck,
  IconEye,
  IconFileText,
  IconBriefcase,
  IconMessage2,
} from "@tabler/icons-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils/cn";
import { getPropertyCycle } from "../api";
import type { AgencyProperty, PropertyActivity } from "../api";

interface PropertyCycleDrawerProps {
  property: AgencyProperty | null;
  onOpenChange: (open: boolean) => void;
}

const money = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const date = (value: string | null) =>
  value ? new Date(value).toLocaleDateString("pt-BR") : "—";

/**
 * Ciclo do imóvel da casa (hub#690): propostas, negócios, contratos e vistorias. Mostra PII do
 * cliente porque a imobiliária é a intermediadora nesses negócios — e o corretor consegue ver
 * que ela abriu, na tela "Quem vê meus dados" dele (hub#691).
 */
export function PropertyCycleDrawer({ property, onOpenChange }: PropertyCycleDrawerProps) {
  const { t } = useTranslation();

  const { data: cycle, isLoading } = useQuery({
    queryKey: ["agency-property-cycle", property?.id],
    queryFn: () => getPropertyCycle(property!.id),
    enabled: property !== null,
  });

  return (
    <Dialog open={property !== null} onOpenChange={(open) => (open ? null : onOpenChange(false))}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>{property?.title}</DialogTitle>
          <DialogDescription>{t("properties.cycle.desc")}</DialogDescription>
        </DialogHeader>

        {isLoading || !cycle ? (
          <p className="py-10 text-center text-sm text-ink-soft">{t("properties.cycle.loading")}</p>
        ) : (
          <div className="space-y-6 py-2">
            <Pulse activity={cycle.activity} />

            <Section
              icon={<IconMessage2 size={16} className="text-brand-600" />}
              title={t("properties.cycle.proposals")}
              empty={cycle.proposals.length === 0 ? t("properties.cycle.noProposals") : null}
            >
              {cycle.proposals.map((proposal) => (
                <li key={proposal.id} className="rounded-xl bg-gray-50/60 px-3 py-2.5">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-bold text-ink">{proposal.clientName}</span>
                    <span className="text-ink-muted">
                      {proposal.amount ? money.format(proposal.amount) : "—"}
                    </span>
                    <span className="text-[10px] font-black uppercase tracking-wider text-ink-soft">
                      {proposal.brokerName} · {date(proposal.createdAt)}
                    </span>
                    <span className="ml-auto rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-ink-soft">
                      {t([`properties.cycle.status.${proposal.status}`, proposal.status])}
                    </span>
                  </div>
                  {proposal.rejectionReason && (
                    <p className="mt-1 text-xs italic text-ink-muted">
                      {t("properties.cycle.rejectedBecause")} {proposal.rejectionReason}
                    </p>
                  )}
                </li>
              ))}
            </Section>

            <Section
              icon={<IconBriefcase size={16} className="text-success" />}
              title={t("properties.cycle.deals")}
              empty={cycle.deals.length === 0 ? t("properties.cycle.noDeals") : null}
            >
              {cycle.deals.map((deal) => (
                <li
                  key={deal.id}
                  className="flex flex-wrap items-center gap-2 rounded-xl bg-gray-50/60 px-3 py-2.5 text-sm"
                >
                  <span className="font-bold text-ink">{deal.clientName}</span>
                  <span className="text-ink-muted">{money.format(deal.amount)}</span>
                  <span className="text-[10px] font-black uppercase tracking-wider text-ink-soft">
                    {deal.brokerName} · {t("properties.cycle.commission")}{" "}
                    {money.format(deal.commissionTotal)}
                  </span>
                  <span className="ml-auto rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-ink-soft">
                    {t([`properties.cycle.status.${deal.status}`, deal.status])}
                  </span>
                </li>
              ))}
            </Section>

            <Section
              icon={<IconFileText size={16} className="text-info" />}
              title={t("properties.cycle.contracts")}
              empty={cycle.contracts.length === 0 ? t("properties.cycle.noContracts") : null}
            >
              {cycle.contracts.map((contract) => (
                <li
                  key={contract.id}
                  className="flex flex-wrap items-center gap-2 rounded-xl bg-gray-50/60 px-3 py-2.5 text-sm"
                >
                  <span className="font-bold text-ink">{contract.internalCode}</span>
                  <span className="text-ink-muted">
                    {contract.value ? money.format(contract.value) : "—"}
                  </span>
                  <span className="text-[10px] font-black uppercase tracking-wider text-ink-soft">
                    {date(contract.startDate)} — {date(contract.endDate)}
                  </span>
                  <span className="ml-auto rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-ink-soft">
                    {t([`properties.cycle.status.${contract.status}`, contract.status])}
                  </span>
                </li>
              ))}
            </Section>

            <Section
              icon={<IconClipboardCheck size={16} className="text-warning" />}
              title={t("properties.cycle.inspections")}
              empty={cycle.inspections.length === 0 ? t("properties.cycle.noInspections") : null}
            >
              {cycle.inspections.map((inspection) => (
                <li
                  key={inspection.id}
                  className="flex flex-wrap items-center gap-2 rounded-xl bg-gray-50/60 px-3 py-2.5 text-sm"
                >
                  <span className="text-ink-muted">
                    {date(inspection.completedAt ?? inspection.scheduledFor)}
                  </span>
                  <span className="ml-auto rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-ink-soft">
                    {t([`properties.cycle.status.${inspection.status}`, inspection.status])}
                  </span>
                </li>
              ))}
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** O pulso vem primeiro: é o que decide se a imobiliária precisa agir neste imóvel. */
function Pulse({ activity }: { activity: PropertyActivity }) {
  const { t } = useTranslation();
  const stalled = activity.daysWithoutActivity !== null && activity.daysWithoutActivity >= 15;

  return (
    <div className="grid gap-3">
      <div
        className={cn(
          "flex items-center gap-3 rounded-2xl px-4 py-3",
          stalled ? "bg-warning-soft text-warning" : "bg-gray-50/60 text-ink-muted",
        )}
      >
        {stalled ? <IconAlertTriangle size={18} /> : <IconEye size={18} />}
        <p className="text-sm font-bold">
          {activity.daysWithoutActivity === null
            ? t("properties.cycle.neverMoved")
            : t("properties.cycle.daysWithout", { count: activity.daysWithoutActivity })}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label={t("properties.cycle.visits30d")} value={activity.visits30d} />
        <Stat label={t("properties.cycle.leads")} value={activity.leads} />
        <Stat label={t("properties.cycle.openProposals")} value={activity.openProposals} />
        <Stat label={t("properties.cycle.rejected")} value={activity.rejectedProposals} />
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border-soft bg-white/60 px-3 py-2">
      <p className="text-[10px] font-black uppercase tracking-widest text-ink-soft">{label}</p>
      <p className="text-lg font-black text-ink">{value}</p>
    </div>
  );
}

function Section({
  icon,
  title,
  empty,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  empty: string | null;
  children: React.ReactNode;
}) {
  return (
    <section className="grid gap-2">
      <h4 className="flex items-center gap-2 text-sm font-bold text-ink">
        {icon}
        {title}
      </h4>
      {empty ? (
        <p className="text-xs text-ink-muted">{empty}</p>
      ) : (
        <ul className="grid gap-1.5">{children}</ul>
      )}
    </section>
  );
}
